import { styled, css, Card, CardProps, Box, BoxProps } from '@mui/material'
import getPaletteColor from '../../../utils/getPaletteColor'
import { getGridValue, getUnitValue } from '../../../utils/processStyles'
import { OVERLAY_TYPE_READ_ONLY } from './ItemCard.constants'
import {
  ItemCardContentProps,
  ItemCardStyledComponentsProps,
  ItemCardStylesProps
} from './ItemCard.types'
import Heading from '../Heading'
import Image from '../Image'
import Text from '../Text'
import ItemCardChildren from './ItemCardChildren'
import IconButton from '../IconButton'
import Tags from '../Tags'
import Alert from '../Alert'
import {
  SideContentWrapperProps,
  SideItemProps
} from '../../../interfaces/sideContent'
import { ItemField } from 'src/interfaces/item'

export const CardWrapper = styled(Card, {
  shouldForwardProp: (prop) =>
    ![
      'childrenGap',
      'gap',
      'item',
      'radius',
      'horizontal',
      'childrenProps',
      'action',
      'isChecked',
      'inCartStyles',
      'hasActions',
      'tags'
    ].includes(String(prop))
})<CardProps & ItemCardStyledComponentsProps>(
  ({ theme, radius, inCartStyles, isChecked, hasActions }) => css`
    overflow: visible;
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 0;
    gap: 0;
    border-radius: ${getUnitValue(radius)};
    background-image: none !important;

    ${isChecked &&
    css`
      border-style: solid;
      border-width: ${getUnitValue(inCartStyles?.width || '2px')};
      border-color: ${getPaletteColor(theme, inCartStyles?.color)};
      margin: -${getUnitValue(inCartStyles?.width || '2px')};
    `}

    ${hasActions &&
    css`
      cursor: pointer;
    `}
  `
)
CardWrapper.defaultProps = {
  elevation: 3,
  radius: 4
}

export const CardContentWrapper = styled(Box)(
  () => css`
    flex: 1;
    display: flex;
    align-items: stretch;
  `
)

export const CardContent = styled(Box, {
  shouldForwardProp: (prop) =>
    !['horizontal', 'isSubitem'].includes(String(prop))
})<ItemCardStylesProps & BoxProps>(
  ({ padding, gap, horizontal, isSubitem, theme }) => css`
    flex: 1;
    display: flex;
    flex-direction: ${horizontal ? 'row' : 'column'};
    justify-content: space-between;
    align-items: stretch;
    padding: ${getUnitValue(padding)};
    gap: ${getUnitValue(gap)};
    max-width: 100%;
    ${isSubitem ? `background-color: ${theme.palette.grey[100]};` : ''}
  `
)
CardContent.defaultProps = {
  padding: 16,
  gap: 8
}

export const CardChildren = styled(Box)<Partial<ItemCardContentProps>>(
  ({ padding, gap, align, columns }) => css`
    display: grid;
    grid-template-columns: repeat(${columns}, auto);
    justify-items: ${getGridValue(align)};
    padding: ${getUnitValue(padding)};
    gap: ${getUnitValue(gap)};

    &:empty {
      display: none;
    }

    > * {
      min-width: 0;
      max-width: 100%;
    }
  `
)
CardChildren.defaultProps = {
  padding: 0,
  gap: 8,
  align: 'inherit',
  columns: 1,
  alignItems: 'start',
  alignContent: 'start'
}

export const CardHeader = styled(ItemCardChildren)(
  ({ align }) => css`
    text-align: ${align || 'left'};
  `
)
CardHeader.defaultProps = {
  fields: [ItemField.Image],
  gap: 8
}

export const CardBody = styled(ItemCardChildren)(
  ({ align }) => css`
    flex: 1;
    text-align: ${align || 'left'};
  `
)
CardBody.defaultProps = {
  fields: [
    ItemField.Name,
    ItemField.Description,
    ItemField.Price,
    ItemField.Tags
  ]
}

export const CardFooter = styled(ItemCardChildren)(() => css``)
CardFooter.defaultProps = {
  fields: []
}

export const CardHeading = styled(Heading, {
  shouldForwardProp: (prop) => !['horizontal'].includes(String(prop))
})(() => css``)

export const Overlay = styled(Box)(
  () => css`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    z-index: 2;
    cursor: not-allowed;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(255, 255, 255, 0.6);
    &.${OVERLAY_TYPE_READ_ONLY} {
      background: rgba(255, 255, 255, 0.1);
    }
    .button-overlay {
      max-width: 100%;
      box-sizing: border-box;
      padding: 4px;
      font-size: 8px;
      white-space: nowrap;
    }
  `
)

export const CardDesc = styled(Text, {
  shouldForwardProp: (prop) => !['horizontal'].includes(String(prop))
})(() => css``)
CardDesc.defaultProps = {
  color: 'text.secondary'
}

export const CardImage = styled(Image)(
  ({ size }) => css`
    ${!size &&
    css`
      min-width: 100px;
    `}
  `
)
CardImage.defaultProps = {
  radius: 4,
  ratio: 75
}

export const InCartIcon = styled(Box)(
  ({ theme, color }) => css`
    z-index: 2;
    position: absolute;
    right: 6px;
    top: 6px;
    font-size: 14px;
    width: 1em;
    height: 1em;
    padding: 4px;
    border-radius: 48px;
    box-sizing: initial;
    color: #fff;
    background-color: ${getPaletteColor(theme, String(color))};
  `
)
InCartIcon.defaultProps = {
  color: 'primary.main'
}

// stockMessage styles
export const StockMessage = styled(Alert)(() => css``)
StockMessage.defaultProps = {
  size: 'small',
  severity: 'warning',
  icon: false,
  align: 'center'
}

// Side Content
export const SideContentWrapper = styled(Box, {
  shouldForwardProp: (prop) => !['float', 'standalone'].includes(String(prop))
})<SideContentWrapperProps>(({ area, float = false, standalone = false }) => {
  let width = 'auto'
  if ((area === 'left' || area === 'right') && float) {
    width = '0'
  }
  return css`
    position: ${standalone && area === 'top' ? 'sticky' : 'relative'};
    top: 0;
    z-index: 100;
    display: flex;
    justify-content: space-between;
    align-items: ${area === 'top' || area === 'left'
      ? 'flex-start'
      : 'flex-end'};
    flex-direction: ${area === 'left' || area === 'right' ? 'column' : 'row'};
    width: ${width};
    height: ${(area === 'top' || area === 'bottom') && float ? 0 : 'auto'};
  `
})
export const SideContentBox = styled(Box)<BoxProps>(
  () => css`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: inherit;
    flex-direction: inherit;

    &:first-of-type {
      justify-content: flex-start;
    }

    &:last-of-type {
      justify-content: flex-end;
    }
  `
)

export const SideItemWrapper = styled(Box)<Partial<SideItemProps> & BoxProps>(
  ({ top, left, right, bottom }) => css`
    position: static;
    padding-top: ${getUnitValue(top)};
    padding-left: ${getUnitValue(left)};
    padding-right: ${getUnitValue(right)};
    padding-bottom: ${getUnitValue(bottom)};
    margin: 0;

    &.hidden {
      visibility: hidden;
    }

    &.SideContentTags {
      border-radius: 3px;

      &.area-top,
      &.area-bottom {
        margin-left: -2px;
        margin-right: -2px;

        &:first-of-type {
          margin-left: 0;
        }
        &:last-of-type {
          margin-right: 0;
        }
      }

      &.area-left,
      &.area-right {
        margin-top: -2px;
        margin-bottom: -2px;

        &:first-of-type {
          margin-top: 0;
        }
        &:last-of-type {
          margin-bottom: 0;
        }
      }

      &.area-top {
        ${top === 0 &&
        css`
          border-top-left-radius: 0;
          border-top-right-radius: 0;
        `}
      }
      &.area-bottom {
        ${bottom === 0 &&
        css`
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        `}
      }
      &.area-left {
        ${left === 0 &&
        css`
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        `}
      }
      &.area-right {
        ${right === 0 &&
        css`
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        `}
      }
    }
  `
)
SideItemWrapper.defaultProps = {
  top: 4,
  left: 4,
  right: 4,
  bottom: 4
}

export const SideIconButton = styled(IconButton)(() => css``)

export const SideTags = styled(Tags)(() => css``)
SideTags.defaultProps = {
  size: 'small',
  radius: 'inherit',
  longText: 5,
  longSize: 8,
  vertical: true
}

export const WarningWrapper = styled(Box)(
  () => css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 5px;
    padding: 0px 18px 10px 0px;
  `
)

export const WarningMessage = styled(Text)(
  ({ theme }) => css`
    color: ${theme.palette.grey[800]};
    font-size: 10px;
  `
)

export const PriceWrapper = styled(Box)(
  () => css`
    display: flex;
    align-items: center;
    gap: 8px;
  `
)

export const PriceText = styled(Text)(
  ({ theme }) => css`
    color: ${theme.palette.text.primary};
    font-weight: 600;
    font-size: 12px;
    line-height: 12px;
  `
)

export const LimitText = styled(Text)(
  ({ theme }) => css`
    color: ${theme.palette.text.primary};
    font-size: 10px;
    font-style: normal;
    font-weight: 400;
    line-height: 130%;
  `
)

export const StockText = styled(Text)(
  ({ theme }) => css`
    display: inline;
    color: ${theme.palette.text.primary};
    font-size: 9px;
    font-style: normal;
    font-weight: 400;
  `
)

export const StockValue = styled(Text)(
  ({ theme }) => css`
    display: inline;
    color: ${theme.palette.text.primary};
    font-size: 9px;
    font-style: normal;
    font-weight: 700;
  `
)

export const DescriptionStockContainer = styled(Box)(
  () => css`
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: flex-end;
    gap: 4px;
  `
)
